// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  CloudUpload,
  Activity,
  PiggyBank,
  BadgeIndianRupee,
  ChevronRight,
  X,
  Wallet,
  Calendar,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { classNames } from '../../utils/helpers';

export const NAV_ITEMS = [
  { to: '/apps/budget', text: 'Dashboard', icon: LayoutGrid },
  { to: '/apps/budget/transactions', text: 'Transactions', icon: BadgeIndianRupee},
  { to: '/apps/budget/analytics', text: 'Analytics', icon: Activity },
  { to: '/apps/categories', text: 'Categories', icon: PiggyBank },
  { to: '/apps/budget-periods', text: 'Budgets', icon: Calendar },
  { to: '/apps/payment-methods', text: 'Wallets', icon: Wallet },
  { to: '/apps/budgetfeed', text: 'Import', icon: CloudUpload },
];

export function SideNav({ isOpen, isMobile, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const [hovering, setHovering] = useState(false);

  // Desktop: expand on hover; Mobile: expand when drawer is open
  const expanded = isMobile ? isOpen : isOpen || hovering;

  const isActive = (to) =>
    to === '/apps/budget'
      ? location.pathname === to // exact for dashboard
      : location.pathname.startsWith(to); // prefix for nested routes

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 bg-black/40 z-30" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        onMouseEnter={() => !isMobile && setHovering(true)}
        onMouseLeave={() => !isMobile && setHovering(false)}
        className={classNames(
          'fixed left-0 top-0 h-full z-40 flex flex-col bg-white border-r border-gray-200',
          'transition-all duration-200 ease-in-out',
          expanded ? 'w-56' : 'w-16',
          isMobile && !isOpen && '-translate-x-full',
          isMobile && isOpen && 'translate-x-0 shadow-xl',
        )}
      >
        {/* Logo / close row */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-gray-100 shrink-0">
          {expanded && (
            <span className="text-sm font-bold text-indigo-600 tracking-wide">
              Budget.io
            </span>
          )}
          {isMobile && (
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 ml-auto"
            >
              <X size={16} />
            </button>
          )}
          {!expanded && !isMobile && (
            <span className="mx-auto text-indigo-600 font-bold text-sm">B</span>
          )}
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {NAV_ITEMS.map(({ to, text, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              onClick={isMobile ? onClose : undefined}
              className={classNames(
                'flex items-center gap-3 mx-2 px-2.5 py-2 rounded-lg text-sm transition-colors',
                isActive(to)
                  ? 'bg-indigo-50 text-indigo-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )}
            >
              <Icon size={18} className="shrink-0" />
              {expanded && <span className="truncate">{text}</span>}
            </Link>
          ))}
        </nav>

        {/* User strip at bottom */}
        {expanded && user && (
          <div className="px-3 py-3 border-t border-gray-100 shrink-0">
            <div className="flex items-center gap-2 px-2">
              <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <span className="text-xs font-semibold text-indigo-700">
                  {user.username?.[0]?.toUpperCase() ?? 'U'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-800 truncate">
                  {user.username ?? user.email}
                </p>
              </div>
              <ChevronRight
                size={13}
                className="text-gray-400 ml-auto shrink-0"
              />
            </div>
          </div>
        )}
      </aside>

      {/* Spacer so main content doesn't go under sidebar on desktop */}
      {!isMobile && (
        <div
          className={classNames(
            'shrink-0 transition-all duration-200',
            expanded ? 'w-56' : 'w-16',
          )}
        />
      )}
    </>
  );
}
